import type { GiftFundingPage } from "../types";
import type { StaffInvitation } from "../types";
import type { AdminCard, AdminCostsResponse, AdminDashboardSummary, AdminSentCardIndex, AdminGiftsResponse, AdminLogPage, AdminOperationPage, AdminRole, AdminStaffMember, AdminTicket, AdminTransactionsResponse, AdminUsersResponse, CardTemplate, CardTemplateCategory, CardTemplatePage, CustomerFeeOverride, CustomerFeeOverrideBundle, CustomerFeeOverrideChange, DashboardWindow, FeeChange, FeeSettingsBundle, KycCase, KycCaseDetail, KycEscalation, KycEscalationKind, MaintenanceControl, NotificationTemplate, TicketDetail, TicketEmailDeliveryMode, TicketEmailPreferencesResponse, TicketPage, TicketPriority, TicketStatus, TicketType, TicketUpload, VersionPolicyControl, Viewer } from "../types";
import { getPortalAppCheckToken } from "./firebaseClient";
import { storeMockTemplateAsset } from "./templateMockMedia";
import { IS_MOCK } from "./runtime";

const wait = (duration = 220) => new Promise((resolve) => window.setTimeout(resolve, duration));

let mockVersion = 4;
let mockRate = "0.02500";
let mockFixedFeeCents = 0;
let mockUpdatedAt = "2026-08-08T19:32:00.000Z";
const mockOverrides = new Map<string, CustomerFeeOverride>();
const mockOverrideHistory = new Map<string, CustomerFeeOverrideChange[]>();
let mockVersionPolicy: VersionPolicyControl = { configured: false, revision: 0, updatedAt: null, allowedVersions: [] };
let mockHistory: FeeChange[] = [
  {
    at: "2026-08-08T19:32:00.000Z",
    actorEmail: "ian@ottagive.com",
    oldRate: "0.02000",
    newRate: "0.02500",
    oldFixedFeeCents: null,
    newFixedFeeCents: null,
    reason: "Q3 pricing review"
  },
  {
    at: "2026-05-14T15:10:00.000Z",
    actorEmail: "finance@ottagive.com",
    oldRate: "0.03000",
    newRate: "0.02000",
    oldFixedFeeCents: null,
    newFixedFeeCents: null,
    reason: "Spring campaign"
  }
];

let mockTickets: TicketDetail[] = [
  {
    id: "ticket-demo-phone", title: "The send button is hidden by the keyboard", description: "On my phone I cannot reach Send after typing a message.",
    type: "bug", status: "inbox", priority: "normal", rank: 1024, source: "mobile", reporterUid: "example-mobile-user", reporterEmail: "Mobile user",
    diagnostics: { platform: "ios", model: "iPhone", appVersion: "1.1.5", screen: "CreateTextPage" },
    createdAt: "2026-09-05T15:00:00.000Z", updatedAt: "2026-09-05T15:00:00.000Z", updatedByEmail: "Mobile user",
    statusChangedAt: null, statusChangedByEmail: "Mobile user", revision: 1, hasAttachment: false, attachment: null, events: []
  },
  {
    id: "ticket-demo-bug", title: "Card receipt is clipped on small screens", description: "The receipt footer is cut off on a narrow device after sending a gift.",
    type: "bug", status: "todo", priority: "high", rank: 1024, source: "admin", reporterUid: "local-super-admin", reporterEmail: "ian@ottagive.com",
    createdAt: "2026-08-22T15:00:00.000Z", updatedAt: "2026-08-22T15:00:00.000Z", updatedByEmail: "ian@ottagive.com",
    statusChangedAt: "2026-08-22T15:00:00.000Z", statusChangedByEmail: "ian@ottagive.com", revision: 1,
    hasAttachment: false, attachment: null,
    events: [{ id: "event-demo-bug", kind: "created", actorEmail: "ian@ottagive.com", actorUid: "local-super-admin", createdAt: "2026-08-22T15:00:00.000Z", revision: 1, changedFields: ["title", "description", "type", "status"], fromStatus: null, toStatus: "todo" }]
  },
  {
    id: "ticket-demo-feature", title: "Add recipient search to gift review", description: "Let operations locate pending gifts by the recipient hint.",
    type: "feature", status: "in_progress", priority: "normal", rank: 1024, source: "admin", reporterUid: "local-super-admin", reporterEmail: "ian@ottagive.com",
    createdAt: "2026-08-20T13:30:00.000Z", updatedAt: "2026-08-22T18:15:00.000Z", updatedByEmail: "ops@ottagive.com",
    statusChangedAt: "2026-08-22T18:15:00.000Z", statusChangedByEmail: "ops@ottagive.com", revision: 2,
    hasAttachment: false, attachment: null,
    events: [
      { id: "event-demo-feature-1", kind: "created", actorEmail: "ian@ottagive.com", actorUid: "local-super-admin", createdAt: "2026-08-20T13:30:00.000Z", revision: 1, changedFields: ["title", "description", "type", "status"], fromStatus: null, toStatus: "todo" },
      { id: "event-demo-feature-2", kind: "status_changed", actorEmail: "ops@ottagive.com", actorUid: "local-support", createdAt: "2026-08-22T18:15:00.000Z", revision: 2, changedFields: ["status"], fromStatus: "todo", toStatus: "in_progress" }
    ]
  }
];

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const appCheckToken = await getPortalAppCheckToken();
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
      ...init?.headers
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error ?? payload.detail ?? "Request failed") as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return payload as T;
}

const mockTemplateOperations = new Map<string, { request: string; result: CardTemplate }>();
let mockTemplateCategories: CardTemplateCategory[] = [
  { id: "b966cd8d-b8bc-4520-94bd-9d90b8066621", name: "Celebrations", count: 1, revision: 1, templates: [
    { id: "fc34991c-87fa-442d-a916-6e0c62ee148d", categoryId: "b966cd8d-b8bc-4520-94bd-9d90b8066621", templateName: "A little something", authorName: "team@ottagive.com", revision: 1,
      pages: [{ uid: "dfb844cb-ae48-4dba-8117-c646cf99169b", type: "text", text: "Today is worth celebrating!", size: 30, fontValue: "fancy", justifyValue: "center", verticalValue: "center" }] }
  ] }
];

export async function getCardTemplateCategories(): Promise<CardTemplateCategory[]> {
  if (IS_MOCK) { await wait(); return structuredClone(mockTemplateCategories); }
  return (await request<{ categories: CardTemplateCategory[] }>("/api/admin/card-templates")).categories;
}

export async function createCardTemplateCategory(name: string): Promise<CardTemplateCategory> {
  if (IS_MOCK) {
    const category = { id: crypto.randomUUID(), name, count: 0, revision: 1, templates: [] };
    mockTemplateCategories = [...mockTemplateCategories, category]; return structuredClone(category);
  }
  const csrfToken = await getCsrfToken();
  return request<CardTemplateCategory>("/api/admin/card-templates/categories", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ name, operationId: crypto.randomUUID() }) });
}

export async function renameCardTemplateCategory(category: CardTemplateCategory, name: string): Promise<void> {
  if (IS_MOCK) { mockTemplateCategories = mockTemplateCategories.map((item) => item.id === category.id ? { ...item, name, revision: item.revision + 1 } : item); return; }
  const csrfToken = await getCsrfToken();
  await request(`/api/admin/card-templates/categories/${encodeURIComponent(category.id)}`, { method: "PUT", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ name, expectedRevision: category.revision, operationId: crypto.randomUUID() }) });
}

export async function deleteCardTemplateCategory(category: CardTemplateCategory): Promise<void> {
  if (IS_MOCK) { mockTemplateCategories = mockTemplateCategories.filter((item) => item.id !== category.id); return; }
  const csrfToken = await getCsrfToken();
  await request(`/api/admin/card-templates/categories/${encodeURIComponent(category.id)}`, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ expectedRevision: category.revision, operationId: crypto.randomUUID() }) });
}

export async function saveCardTemplate(categoryId: string, templateName: string, pages: CardTemplatePage[], existing?: CardTemplate, operationId: string = crypto.randomUUID()): Promise<CardTemplate> {
  if (IS_MOCK) {
    const request = JSON.stringify({ categoryId, templateName, pages, id: existing?.id, revision: existing?.revision });
    const replay = mockTemplateOperations.get(operationId);
    if (replay) { if (replay.request !== request) throw new Error("Operation ID belongs to a different request"); return structuredClone(replay.result); }
    if (existing && mockTemplateCategories.flatMap((category) => category.templates).find((template) => template.id === existing.id)?.revision !== existing.revision) throw new Error("Template changed; your draft is retained");
    const result: CardTemplate = { id: existing?.id ?? operationId, categoryId, templateName, pages: structuredClone(pages), authorName: existing?.authorName ?? "ian@ottagive.com", revision: (existing?.revision ?? 0) + 1 };
    mockTemplateCategories = mockTemplateCategories.map((category) => category.id === categoryId ? { ...category, count: existing ? category.count : category.count + 1, templates: existing ? category.templates.map((item) => item.id === existing.id ? result : item) : [...category.templates, result] } : category);
    mockTemplateOperations.set(operationId, { request, result: structuredClone(result) });
    return structuredClone(result);
  }
  const csrfToken = await getCsrfToken();
  const path = `/api/admin/card-templates/categories/${encodeURIComponent(categoryId)}/templates${existing ? `/${encodeURIComponent(existing.id)}` : ""}`;
  return request<CardTemplate>(path, { method: existing ? "PUT" : "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ templateName, pages, ...(existing ? { expectedRevision: existing.revision } : {}), operationId }) });
}

export async function deleteCardTemplate(template: CardTemplate): Promise<void> {
  if (IS_MOCK) { mockTemplateCategories = mockTemplateCategories.map((category) => category.id === template.categoryId ? { ...category, count: category.count - 1, templates: category.templates.filter((item) => item.id !== template.id) } : category); return; }
  const csrfToken = await getCsrfToken();
  await request(`/api/admin/card-templates/categories/${encodeURIComponent(template.categoryId)}/templates/${encodeURIComponent(template.id)}`, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ expectedRevision: template.revision, operationId: crypto.randomUUID() }) });
}

export async function uploadCardTemplateImage(file: File, options: { destination?: "multimedia"; operationId?: string } = {}): Promise<string> {
  if (file.size > 5 * 1024 * 1024) throw new Error("Image must be 5 MB or smaller");
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Choose a JPEG, PNG, or WebP image");
  if (IS_MOCK) return storeMockTemplateAsset(file, options.operationId ?? crypto.randomUUID());
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(file);
  });
  const csrfToken = await getCsrfToken();
  const result = await request<{ url: string }>("/api/admin/card-templates/assets", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ contentType: file.type, data, operationId: options.operationId ?? crypto.randomUUID(), ...(options.destination ? { destination: options.destination } : {}) }) });
  return result.url;
}

export async function uploadCardTemplateMedia(file: File, options: { destination?: "multimedia"; operationId?: string } = {}): Promise<string> {
  if (!["audio/wav", "video/mp4", "video/quicktime"].includes(file.type)) throw new Error("Choose a WAV, MP4, or MOV file");
  if (file.size > 25 * 1024 * 1024) throw new Error("Media must be 25 MB or smaller");
  if (IS_MOCK) return storeMockTemplateAsset(file, options.operationId ?? crypto.randomUUID());
  const csrfToken = await getCsrfToken();
  const result = await request<{ url: string }>("/api/admin/card-templates/media", {
    method: "POST", headers: { "Content-Type": file.type, "X-CSRF-Token": csrfToken, "X-Operation-Id": options.operationId ?? crypto.randomUUID(), ...(options.destination ? { "X-Template-Destination": options.destination } : {}) }, body: file
  });
  return result.url;
}

export async function getViewer(): Promise<Viewer | null> {
  if (IS_MOCK) {
    await wait(120);
    return {
      uid: "local-super-admin",
      email: "ian@ottagive.com",
      role: new URLSearchParams(window.location.search).get("mockRole") === "cardTemplateCreator" ? "cardTemplateCreator" : "superAdmin",
      environment: "development"
    };
  }
  try {
    return await request<Viewer>("/api/session");
  } catch (error) {
    if ((error as { status?: number }).status === 401) return null;
    throw error;
  }
}

export async function getFeeSettings(): Promise<FeeSettingsBundle> {
  if (IS_MOCK) {
    await wait();
    return {
      current: { commissionRate: mockRate, fixedFeeCents: mockFixedFeeCents, version: mockVersion, updatedAt: mockUpdatedAt },
      history: [...mockHistory]
    };
  }
  const [current, history] = await Promise.all([
    request<FeeSettingsBundle["current"]>("/api/admin/fee-settings"),
    request<FeeSettingsBundle["history"]>("/api/admin/fee-settings/history")
  ]);
  return { current, history };
}

export interface UpdateFeeInput {
  commissionRate: string;
  /** Omitted means "leave the flat fee alone", never "set it to zero". */
  fixedFeeCents?: number;
  expectedVersion: number;
  operationId: string;
  reason: string;
  csrfToken?: string;
}

export async function updateFeeSettings(input: UpdateFeeInput, actorEmail: string): Promise<FeeSettingsBundle> {
  if (IS_MOCK) {
    await wait(500);
    if (input.expectedVersion !== mockVersion) {
      const error = new Error("Another admin changed the commission rate") as Error & { status?: number };
      error.status = 409;
      throw error;
    }
    const oldRate = mockRate;
    const oldFixedFeeCents = mockFixedFeeCents;
    mockRate = input.commissionRate;
    if (input.fixedFeeCents !== undefined) mockFixedFeeCents = input.fixedFeeCents;
    mockVersion += 1;
    mockUpdatedAt = new Date().toISOString();
    mockHistory = [
      {
        at: mockUpdatedAt,
        actorEmail,
        oldRate,
        newRate: mockRate,
        oldFixedFeeCents,
        newFixedFeeCents: mockFixedFeeCents,
        reason: input.reason
      },
      ...mockHistory
    ];
    return getFeeSettings();
  }
  await request<FeeSettingsBundle["current"]>("/api/admin/fee-settings", {
    method: "PUT",
    headers: input.csrfToken ? { "X-CSRF-Token": input.csrfToken } : {},
    body: JSON.stringify(input)
  });
  return getFeeSettings();
}

function mockOverrideFor(customerGuid: string): CustomerFeeOverride {
  const stored = mockOverrides.get(customerGuid);
  return {
    customerGuid,
    commissionRate: stored?.commissionRate ?? null,
    fixedFeeCents: stored?.fixedFeeCents ?? null,
    version: stored?.version ?? 0,
    effectiveCommissionRate: stored?.commissionRate ?? mockRate,
    effectiveFixedFeeCents: stored?.fixedFeeCents ?? mockFixedFeeCents,
    rateSource: stored?.commissionRate != null ? "customer" : "platform",
    fixedFeeSource: stored?.fixedFeeCents != null ? "customer" : "platform",
    updatedAt: stored?.updatedAt ?? null
  };
}

export async function getCustomerFeeOverride(customerGuid: string): Promise<CustomerFeeOverrideBundle> {
  if (IS_MOCK) {
    await wait();
    return { current: mockOverrideFor(customerGuid), history: mockOverrideHistory.get(customerGuid) ?? [] };
  }
  const [current, history] = await Promise.all([
    request<CustomerFeeOverride>(`/api/admin/customers/${encodeURIComponent(customerGuid)}/fee-override`),
    request<CustomerFeeOverrideChange[]>(`/api/admin/customers/${encodeURIComponent(customerGuid)}/fee-override/history`)
  ]);
  return { current, history };
}

export interface UpdateCustomerFeeInput {
  /** Null means "follow the platform value" for that component. */
  commissionRate: string | null;
  fixedFeeCents: number | null;
  expectedVersion: number;
  operationId: string;
  reason: string;
  csrfToken?: string;
}

export async function updateCustomerFeeOverride(
  customerGuid: string,
  input: UpdateCustomerFeeInput,
  actorEmail: string
): Promise<CustomerFeeOverrideBundle> {
  if (IS_MOCK) {
    await wait(500);
    const existing = mockOverrides.get(customerGuid);
    if (input.expectedVersion !== (existing?.version ?? 0)) {
      const error = new Error("Another admin changed this customer's pricing") as Error & { status?: number };
      error.status = 409;
      throw error;
    }
    const at = new Date().toISOString();
    mockOverrides.set(customerGuid, {
      ...mockOverrideFor(customerGuid),
      commissionRate: input.commissionRate,
      fixedFeeCents: input.fixedFeeCents,
      version: (existing?.version ?? 0) + 1,
      updatedAt: at
    });
    mockOverrideHistory.set(customerGuid, [
      {
        at,
        actorEmail,
        oldRate: existing?.commissionRate ?? null,
        newRate: input.commissionRate,
        oldFixedFeeCents: existing?.fixedFeeCents ?? null,
        newFixedFeeCents: input.fixedFeeCents,
        existedBefore: existing !== undefined,
        reason: input.reason
      },
      ...(mockOverrideHistory.get(customerGuid) ?? [])
    ]);
    return getCustomerFeeOverride(customerGuid);
  }
  await request<CustomerFeeOverride>(`/api/admin/customers/${encodeURIComponent(customerGuid)}/fee-override`, {
    method: "PUT",
    headers: input.csrfToken ? { "X-CSRF-Token": input.csrfToken } : {},
    body: JSON.stringify(input)
  });
  return getCustomerFeeOverride(customerGuid);
}

export async function getCsrfToken(): Promise<string> {
  if (IS_MOCK) return "local-csrf-token";
  const result = await request<{ csrfToken: string }>("/api/session/csrf");
  return result.csrfToken;
}

export const ADMIN_PAGE_SIZE = 50;

export async function getAdminUsers(filters: { search?: string; state?: string; skip?: number; limit?: number } = {}): Promise<AdminUsersResponse> {
  if (IS_MOCK) {
    await wait();
    return {
      limit: filters.limit ?? ADMIN_PAGE_SIZE,
      skip: filters.skip ?? 0,
      hasNext: false,
      users: [
        {
          id: 1,
          customerGuid: "cust_dev_01",
          firebaseUid: "firebase-dev-user-01",
          emailMasked: "ia•••@example.com",
          phoneNumber: "+13125550123",
          name: "Ian Example",
          state: "verified",
          createdAt: "2026-08-01T15:20:00Z",
          updatedAt: "2026-08-08T10:00:00Z",
          identityVerificationGuid: "idv_dev_01",
          personaInquiryId: "inq_dev_01",
          identityCreatedAt: "2026-08-01T15:24:00Z",
          banned: false
        }
      ].filter((user) => !filters.search || JSON.stringify(user).toLowerCase().includes(filters.search.toLowerCase()))
    };
  }
  const query = new URLSearchParams();
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.state?.trim()) query.set("state", filters.state.trim());
  query.set("skip", String(filters.skip ?? 0));
  query.set("limit", String(filters.limit ?? ADMIN_PAGE_SIZE));
  const suffix = query.size ? `?${query.toString()}` : "";
  return request<AdminUsersResponse>(`/api/admin/users${suffix}`);
}

export async function getAdminTransactions(filters: { search?: string; status?: string; displayType?: string; skip?: number; limit?: number } = {}): Promise<AdminTransactionsResponse> {
  if (IS_MOCK) {
    await wait();
    const transactions = [{
      transferId: 2841, guid: "trf_dev_8c712aa", customerGuid: "cust_dev_01", customerName: "Ian Example",
      email: "recipient@example.com", displayType: "giftCardSent", transferType: "book", movementType: "customer_to_customer",
      quoteGuid: "qte_dev_39fd1", externalAccountGuid: null, amountCents: 5000, estimatedAmountCents: 5000,
      feeCents: 100, asset: "USD", side: "debit", status: "completed", state: "completed",
      sourceAccountGuid: "act_dev_main_01", destinationAccountGuid: "act_dev_staged_01", paymentRail: "book",
      parentTransferId: null, createdAt: "2026-08-08T15:20:00Z", updatedAt: "2026-08-08T15:20:08Z"
    }];
    const matching = transactions.filter((transaction) =>
      (!filters.search || JSON.stringify(transaction).toLowerCase().includes(filters.search.toLowerCase()))
      && (!filters.status || transaction.status === filters.status)
      && (!filters.displayType || transaction.displayType === filters.displayType)
    );
    const skip = filters.skip ?? 0;
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    return { totalCount: matching.length, transactions: matching.slice(skip, skip + limit), skip, limit, hasNext: skip + limit < matching.length };
  }
  const query = new URLSearchParams();
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.status?.trim()) query.set("status", filters.status.trim());
  if (filters.displayType?.trim()) query.set("displayType", filters.displayType.trim());
  query.set("skip", String(filters.skip ?? 0));
  query.set("limit", String(filters.limit ?? ADMIN_PAGE_SIZE));
  return request<AdminTransactionsResponse>(`/api/admin/transactions${query.size ? `?${query}` : ""}`);
}

const MOCK_SENT_CARDS: AdminCard[] = [
  { id: "card-dev-legacy", senderUid: "firebase-dev-user-01", recipientUid: null, sender: { uid: "firebase-dev-user-01", name: "Ian Example", photoUrl: null, phoneNumber: "+13125550123" }, recipient: { uid: null, name: null, photoUrl: null, phoneNumber: "+13125550124" }, amountCents: 5000, transferId: "2839", redeemed: false, respondedTo: false, sentAt: "2026-08-01T15:20:00Z" },
  { id: "card-dev-01", senderUid: "firebase-dev-user-01", recipientUid: "firebase-dev-user-02", sender: { uid: "firebase-dev-user-01", name: "Ian Example", photoUrl: null, phoneNumber: "+13125550123" }, recipient: { uid: "firebase-dev-user-02", name: "Riley Example", photoUrl: null, phoneNumber: "+13125550124" }, amountCents: 5000, transferId: "2841", redeemed: true, respondedTo: true, sentAt: "2026-08-08T15:20:00Z" },
  { id: "card-dev-orphan", senderUid: "firebase-dev-user-01", recipientUid: null, sender: { uid: "firebase-dev-user-01", name: "Ian Example", photoUrl: null, phoneNumber: "+13125550123" }, recipient: null, amountCents: 1500, transferId: "1907", redeemed: false, respondedTo: false, sentAt: "2026-07-14T09:00:00Z" }
];

export async function getAdminGifts(filters: { search?: string; attentionOnly?: boolean; mode?: "lifecycle" | "legacy"; skip?: number; limit?: number } = {}): Promise<AdminGiftsResponse> {
  if (IS_MOCK) {
    await wait();
    const gifts = [{
      transferId: 2839, transferGuid: "trf_legacy_dev_01", giftId: null, firestoreCardId: "card-dev-legacy",
      senderCustomerGuid: "cust_dev_01", senderFirebaseUid: "firebase-dev-user-01", recipientCustomerGuid: null,
      recipientHints: [], amountCents: 5000, currency: "USD", giftState: "legacy_unbound" as const,
      bindingStatus: "legacy_unbound" as const, provenanceStatus: "unavailable" as const,
      attentionReasons: ["legacy_gift_requires_review"], operation: null, movements: [],
      createdAt: "2026-08-01T15:20:00Z", updatedAt: "2026-08-01T15:20:08Z"
    }, {
      transferId: 2841, transferGuid: "trf_dev_8c712aa", giftId: "gift-dev-01", firestoreCardId: "card-dev-01",
      senderCustomerGuid: "cust_dev_01", senderFirebaseUid: "firebase-dev-user-01", recipientCustomerGuid: null,
      recipientHints: ["r***@example.com"], amountCents: 5000, currency: "USD", giftState: "pending",
      bindingStatus: "verified" as const, provenanceStatus: "complete" as const, attentionReasons: [],
      operation: { id: "op-dev-01", operationType: "send", state: "completed", attempts: 1, errorCode: null, errorMessage: null, createdAt: "2026-08-08T15:20:00Z", updatedAt: "2026-08-08T15:20:08Z" },
      movements: [{ id: "mov-dev-01", purpose: "gift_value", amountCents: 5000, currency: "USD", state: "completed", providerState: "completed", providerFailureCode: null, sourceKind: "customer_cash", sourceDisplayLabel: "Ottagive Cash", destinationKind: "gift_reserve", destinationDisplayLabel: "Gift reserve", initiatedAt: "2026-08-08T15:20:00Z", settledAt: "2026-08-08T15:20:08Z", updatedAt: "2026-08-08T15:20:08Z" }],
      createdAt: "2026-08-08T15:20:00Z", updatedAt: "2026-08-08T15:20:08Z"
    }, {
      // Adopted, no recipient customer, no movement legs. Reads as three faults and is
      // none: adoption moves no money, the customer resolves at redemption, and a gift
      // funded before the movement ledger has no legs to show.
      transferId: 2843, transferGuid: "trf_dev_5f01c93", giftId: "gift-dev-02", firestoreCardId: "card-dev-02",
      senderCustomerGuid: "cust_dev_01", senderFirebaseUid: "firebase-dev-user-01", recipientCustomerGuid: null,
      recipientHints: ["r***@example.com"], amountCents: 2500, currency: "USD", giftState: "adopted",
      bindingStatus: "verified" as const, provenanceStatus: "unavailable" as const, attentionReasons: [],
      operation: { id: "op-dev-02", operationType: "claim", state: "succeeded", attempts: 1, errorCode: null, errorMessage: null, createdAt: "2026-08-09T09:04:00Z", updatedAt: "2026-08-09T09:04:06Z" },
      movements: [],
      createdAt: "2026-08-02T11:02:00Z", updatedAt: "2026-08-09T09:04:06Z"
    }];
    const matching = gifts.filter((gift) =>
      (!filters.search || JSON.stringify(gift).toLowerCase().includes(filters.search.toLowerCase()))
      && (!filters.attentionOnly || gift.attentionReasons.length > 0)
    );
    const modeMatching = matching.filter((gift) => filters.mode === "legacy" ? gift.bindingStatus === "legacy_unbound" : filters.mode === "lifecycle" ? gift.bindingStatus !== "legacy_unbound" : true);
    const skip = filters.skip ?? 0;
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const hasNext = skip + limit < modeMatching.length;
    return { gifts: modeMatching.slice(skip, skip + limit), totalCount: modeMatching.length, attentionCount: modeMatching.filter((gift) => gift.attentionReasons.length > 0).length, skip, limit, hasNext, nextSkip: hasNext ? skip + limit : null };
  }
  const query = new URLSearchParams();
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.attentionOnly) query.set("attentionOnly", "true");
  if (filters.mode) query.set("mode", filters.mode);
  query.set("skip", String(filters.skip ?? 0));
  query.set("limit", String(filters.limit ?? ADMIN_PAGE_SIZE));
  return request<AdminGiftsResponse>(`/api/admin/gifts${query.size ? `?${query}` : ""}`);
}

export async function exportAdminTransactions(input: { search: string; status: string; displayType: string; reason: string; confirmation: string }): Promise<Pick<AdminTransactionsResponse, "transactions" | "totalCount"> & { exportedCount: number }> {
  if (IS_MOCK) return { totalCount: 0, exportedCount: 0, transactions: [] };
  const csrfToken = await getCsrfToken();
  return request("/api/admin/transactions/export", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function getAdminOperations(cursor?: string): Promise<AdminOperationPage> {
  if (IS_MOCK) {
    await wait();
    return { operations: [], nextCursor: null };
  }
  const query = cursor ? `?${new URLSearchParams({ cursor })}` : "";
  return request<AdminOperationPage>(`/api/admin/operations${query}`);
}

export async function getTickets(status: TicketStatus, type?: TicketType, cursor?: string): Promise<TicketPage> {
  if (IS_MOCK) {
    await wait();
    return {
      tickets: mockTickets.filter((ticket) => ticket.status === status && (!type || ticket.type === type)).sort((left, right) => (left.rank ?? 0) - (right.rank ?? 0)).map(({ events: _events, ...ticket }) => ticket),
      nextCursor: null
    };
  }
  const query = new URLSearchParams({ status });
  if (type) query.set("type", type);
  if (cursor) query.set("cursor", cursor);
  return request<TicketPage>(`/api/admin/tickets?${query.toString()}`);
}

export async function getTicket(ticketId: string): Promise<TicketDetail> {
  if (IS_MOCK) {
    await wait(120);
    const ticket = mockTickets.find((entry) => entry.id === ticketId);
    if (!ticket) throw Object.assign(new Error("Ticket not found"), { status: 404 });
    return structuredClone(ticket);
  }
  return request<TicketDetail>(`/api/admin/tickets/${encodeURIComponent(ticketId)}`);
}

export async function uploadTicketImage(file: File): Promise<TicketUpload> {
  if (IS_MOCK) {
    await wait(350);
    return { uploadId: crypto.randomUUID(), filename: file.name, contentType: (file.type || "image/png") as TicketUpload["contentType"], sizeBytes: file.size, width: 1200, height: 800, expiresAt: new Date(Date.now() + 30 * 60_000).toISOString() };
  }
  const csrfToken = await getCsrfToken();
  return request<TicketUpload>("/api/admin/ticket-uploads", {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream", "X-CSRF-Token": csrfToken, "X-File-Name": encodeURIComponent(file.name) },
    body: file
  });
}

export async function createTicket(input: { title: string; description: string; type: TicketType; priority: TicketPriority; uploadId?: string }, actor: Viewer): Promise<TicketDetail> {
  if (IS_MOCK) {
    await wait(300);
    const now = new Date().toISOString();
    const id = crypto.randomUUID();
    const ticket: TicketDetail = {
      id, ...input, status: "todo", rank: -Date.now(), source: "admin", reporterUid: actor.uid, reporterEmail: actor.email,
      createdAt: now, updatedAt: now, updatedByEmail: actor.email, statusChangedAt: now, statusChangedByEmail: actor.email,
      revision: 1, hasAttachment: Boolean(input.uploadId), attachment: input.uploadId ? { id: crypto.randomUUID(), filename: "screenshot.png", contentType: "image/png", sizeBytes: 0, width: 1200, height: 800 } : null,
      events: [{ id: crypto.randomUUID(), kind: "created", actorEmail: actor.email, actorUid: actor.uid, createdAt: now, revision: 1, changedFields: ["title", "description", "type", "status", ...(input.uploadId ? ["attachment"] : [])], fromStatus: null, toStatus: "todo" }]
    };
    mockTickets = [ticket, ...mockTickets];
    return structuredClone(ticket);
  }
  const csrfToken = await getCsrfToken();
  return request<TicketDetail>("/api/admin/tickets", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ ...input, operationId: crypto.randomUUID() }) });
}

export async function updateTicket(input: { ticketId: string; expectedRevision: number; title: string; description: string; type: TicketType; priority: TicketPriority; attachmentAction: "keep" | "remove" | "replace"; uploadId?: string }, actor: Viewer): Promise<TicketDetail> {
  if (IS_MOCK) {
    await wait(300);
    const index = mockTickets.findIndex((entry) => entry.id === input.ticketId);
    if (index < 0) throw Object.assign(new Error("Ticket not found"), { status: 404 });
    if (mockTickets[index].revision !== input.expectedRevision) throw Object.assign(new Error("Another admin changed this ticket"), { status: 409 });
    const previous = mockTickets[index]; const now = new Date().toISOString(); const revision = previous.revision + 1;
    const attachment = input.attachmentAction === "remove" ? null : input.attachmentAction === "replace" ? { id: crypto.randomUUID(), filename: "screenshot.png", contentType: "image/png" as const, sizeBytes: 0, width: 1200, height: 800 } : previous.attachment;
    mockTickets[index] = { ...previous, title: input.title, description: input.description, type: input.type, priority: input.priority, attachment, hasAttachment: Boolean(attachment), updatedAt: now, updatedByEmail: actor.email, revision, events: [...previous.events, { id: crypto.randomUUID(), kind: "content_updated", actorEmail: actor.email, actorUid: actor.uid, createdAt: now, revision, changedFields: ["title", "description", "type", "priority", "attachment"], fromStatus: null, toStatus: null }] };
    return structuredClone(mockTickets[index]);
  }
  const csrfToken = await getCsrfToken();
  const { ticketId, ...body } = input;
  return request<TicketDetail>(`/api/admin/tickets/${encodeURIComponent(ticketId)}`, { method: "PUT", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ ...body, operationId: crypto.randomUUID() }) });
}

export async function moveTicket(input: { ticketId: string; expectedRevision: number; status: TicketStatus; beforeTicketId?: string | null }, actor: Viewer): Promise<TicketDetail> {
  if (IS_MOCK) {
    await wait(220);
    const index = mockTickets.findIndex((entry) => entry.id === input.ticketId);
    if (index < 0) throw Object.assign(new Error("Ticket not found"), { status: 404 });
    const previous = mockTickets[index];
    if (previous.revision !== input.expectedRevision) throw Object.assign(new Error("Another admin changed this ticket"), { status: 409 });
    const now = new Date().toISOString(); const revision = previous.revision + 1;
    const destination = mockTickets.filter((entry) => entry.status === input.status && entry.id !== input.ticketId).sort((left, right) => (left.rank ?? 0) - (right.rank ?? 0));
    const position = input.beforeTicketId === null ? destination.length : input.beforeTicketId ? Math.max(0, destination.findIndex((entry) => entry.id === input.beforeTicketId)) : 0;
    destination.splice(position, 0, previous);
    destination.forEach((entry, order) => { entry.rank = (order + 1) * 1024; });
    const statusChanged = previous.status !== input.status;
    mockTickets[index] = { ...previous, status: input.status, rank: (position + 1) * 1024, revision, updatedAt: now, updatedByEmail: actor.email, ...(statusChanged ? { statusChangedAt: now, statusChangedByEmail: actor.email } : {}), events: [...previous.events, { id: crypto.randomUUID(), kind: statusChanged ? "status_changed" : "order_changed", actorEmail: actor.email, actorUid: actor.uid, createdAt: now, revision, changedFields: statusChanged ? ["status", "rank"] : ["rank"], fromStatus: previous.status, toStatus: input.status }] };
    return structuredClone(mockTickets[index]);
  }
  const csrfToken = await getCsrfToken();
  return request<TicketDetail>(`/api/admin/tickets/${encodeURIComponent(input.ticketId)}/status`, { method: "PATCH", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ expectedRevision: input.expectedRevision, status: input.status, beforeTicketId: input.beforeTicketId, operationId: crypto.randomUUID() }) });
}

let mockTicketEmailMode: TicketEmailDeliveryMode = "immediate";

export async function getTicketEmailPreferences(): Promise<TicketEmailPreferencesResponse> {
  if (IS_MOCK) return { preferences: [{ uid: "local-super-admin", email: "ian@ottagive.com", role: "superAdmin", deliveryMode: mockTicketEmailMode, updatedAt: new Date().toISOString(), canEdit: true }], emailDeliveryEnabled: true, digestSchedule: { localTime: "11:00 PM", timeZone: "America/Chicago" } };
  return request<TicketEmailPreferencesResponse>("/api/admin/ticket-email-preferences");
}

export async function updateTicketEmailPreference(uid: string, deliveryMode: TicketEmailDeliveryMode): Promise<void> {
  if (IS_MOCK) { await wait(); mockTicketEmailMode = deliveryMode; return; }
  const csrfToken = await getCsrfToken();
  await request(`/api/admin/ticket-email-preferences/${encodeURIComponent(uid)}`, { method: "PUT", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ deliveryMode, operationId: crypto.randomUUID() }) });
}

export async function getAdminDashboardSummary(windowKey: DashboardWindow): Promise<AdminDashboardSummary> {
  if (IS_MOCK) {
    await wait();
    const now = new Date();
    const duration = windowKey === "1d" ? 86_400_000 : windowKey === "30d" ? 30 * 86_400_000 : 7 * 86_400_000;
    const asOf = now.toISOString();
    const summary: AdminDashboardSummary = {
      generatedAt: asOf,
      window: { key: windowKey, startsAt: new Date(now.getTime() - duration).toISOString(), endsAt: asOf },
      registrations: { available: true, asOf, data: { currentTotal: 248, createdInWindow: windowKey === "1d" ? 3 : windowKey === "7d" ? 19 : 61, coverageStartedAt: "2025-12-16T20:42:00Z", profilesWithoutAuth: 0 } },
      operations: { available: true, asOf, data: { failedInWindow: 1, unknownInWindow: 0, strandedRequestedInWindow: 1 } },
      kyc: { available: true, asOf, data: { currentByRawState: { verified: 180, reviewing: 7, waiting: 11, rejected: 3, future_provider_state: 1 }, startedInWindow: 14, verifiedInWindow: 9, verificationCoverageStartedAt: "2026-08-01T00:00:00Z" } },
      gifts: { available: true, asOf, data: { totalByRawState: { pending: 32, awaiting_claim: 44, adopted: 18, claimed: 122, reversed: 4, failed: 3 }, createdInWindowByRawState: { pending: 4, awaiting_claim: 5, adopted: 2, claimed: 7, failed: 1 }, attentionCurrent: 3 } },
      transactions: { available: true, asOf, data: { createdInWindowByRawStatus: { completed: 88, pending: 3, failed: 2 }, updatedInWindowByRawStatus: { failed: 2, rejected: 1, reversed: 1 }, staleBefore24hByRawStatus: { pending: 2, processing: 1 } } }
    };
    const variant = new URLSearchParams(globalThis.location?.search ?? "").get("dashboard");
    if (variant === "partial") summary.transactions = { available: false, reason: "timeout" };
    if (variant === "unavailable") {
      summary.registrations = { available: false, reason: "not_indexed" };
      summary.operations = { available: false, reason: "unavailable" };
      summary.kyc = summary.gifts = summary.transactions = { available: false, reason: "not_deployed" };
    }
    if (variant === "empty") {
      summary.registrations = { available: true, asOf, data: { currentTotal: 0, createdInWindow: 0, coverageStartedAt: null, profilesWithoutAuth: 0 } };
      summary.operations = { available: true, asOf, data: { failedInWindow: 0, unknownInWindow: 0, strandedRequestedInWindow: 0 } };
      summary.kyc = { available: true, asOf, data: { currentByRawState: {}, startedInWindow: 0, verifiedInWindow: 0, verificationCoverageStartedAt: asOf } };
      summary.gifts = { available: true, asOf, data: { totalByRawState: {}, createdInWindowByRawState: {}, attentionCurrent: 0 } };
      summary.transactions = { available: true, asOf, data: { createdInWindowByRawStatus: {}, updatedInWindowByRawStatus: {}, staleBefore24hByRawStatus: {} } };
    }
    if (variant === "stale" && summary.registrations.available) summary.registrations.asOf = "2026-08-01T03:15:00Z";
    return summary;
  }
  return request<AdminDashboardSummary>(`/api/admin/dashboard-summary?window=${windowKey}`);
}

/**
 * The card projection for the senders currently on screen. Scoping the read to those
 * senders is what makes the projection joinable to the gift queue at all.
 */
export async function getAdminSentCards(identifiers: { senderUids: string[]; senderCustomerGuids: string[]; cardIds: string[] }): Promise<AdminSentCardIndex> {
  if (IS_MOCK) {
    await wait();
    return { cards: MOCK_SENT_CARDS.filter((card) => (card.senderUid && identifiers.senderUids.includes(card.senderUid)) || identifiers.cardIds.includes(card.id)), truncatedSenders: false };
  }
  const query = new URLSearchParams();
  if (identifiers.senderUids.length) query.set("senderUids", identifiers.senderUids.join(","));
  if (identifiers.senderCustomerGuids.length) query.set("senderCustomerGuids", identifiers.senderCustomerGuids.join(","));
  if (identifiers.cardIds.length) query.set("cardIds", identifiers.cardIds.join(","));
  return request<AdminSentCardIndex>(`/api/admin/cards?${query.toString()}`);
}

export async function getAdminLogs(filters: { errorsOnly?: boolean; pageToken?: string } = {}): Promise<AdminLogPage> {
  if (IS_MOCK) {
    await wait();
    if (new URLSearchParams(globalThis.location?.search ?? "").get("dashboard") === "capped") {
      return { entries: Array.from({ length: ADMIN_PAGE_SIZE }, (_, index) => ({
        timestamp: new Date(Date.now() - index * 60_000).toISOString(), severity: "WARNING", service: "ottagive-api-dev",
        method: "GET", path: "/api/mock", status: 500, message: "Mock capped warning", revision: "mock", traceId: null,
        spanId: null, insertId: `mock-${index}`, requestId: null, latencyMs: 25, requestSizeBytes: null,
        responseSizeBytes: null, protocol: "HTTP/2", source: "mock", environment: "development", errorType: "MockError",
        upstream: null, failureCodes: []
      })), nextPageToken: "mock-next", windowHours: 24, minimumSeverity: "WARNING" };
    }
    return { entries: [], nextPageToken: null, windowHours: 24, minimumSeverity: filters.errorsOnly ? "ERROR" : "WARNING" };
  }
  const query = new URLSearchParams();
  if (filters.errorsOnly) query.set("errorsOnly", "true");
  if (filters.pageToken) query.set("pageToken", filters.pageToken);
  return request<AdminLogPage>(`/api/admin/logs${query.size ? `?${query}` : ""}`);
}

export async function getAdminCosts(month = new Date().toISOString().slice(0, 7)): Promise<AdminCostsResponse> {
  if (IS_MOCK) {
    const [year, monthNumber] = month.split("-").map(Number);
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    const nextMonth = new Date(Date.UTC(year, monthNumber, 1));
    const period = {
      month,
      startDate: `${month}-01`,
      endDate: month === currentMonth ? now.toISOString().slice(0, 10) : new Date(nextMonth.getTime() - 86_400_000).toISOString().slice(0, 10),
      isCurrent: month === currentMonth
    };
    return {
    configured: true,
    billingAccount: "018A61-83FFDE-44A725",
    dataset: "ottagive-dev.ottagive_billing",
    currency: "USD",
    period,
    dataThrough: new Date().toISOString().slice(0, 10),
    monthToDate: { key: "monthToDate", grossCost: 86.42, credits: -18.2, netCost: 68.22 },
    services: [
      { key: "Cloud Run", grossCost: 31.5, credits: -5, netCost: 26.5 },
      { key: "Firebase", grossCost: 23.72, credits: -8.2, netCost: 15.52 },
      { key: "BigQuery", grossCost: 8.2, credits: -5, netCost: 3.2 }
    ],
    projectCosts: [
      { key: "ottagive-dev", grossCost: 21.2, credits: -6, netCost: 15.2 },
      { key: "ottagive-prod", grossCost: 65.22, credits: -12.2, netCost: 53.02 }
    ],
    dailyCosts: [],
    neon: {
      configured: true,
      period,
      organization: { id: "org-local", name: "Ottagive", plan: "launch" },
      project: {
        id: "plain-union-58175914", name: "ottagive-dev", region: "aws-us-east-2", pgVersion: 17,
        storageBytes: 32_590_960, dataTransferBytes: 1_834_937, activeTimeSeconds: 19_588,
        computeTimeSeconds: 4_907, historyRetentionSeconds: 21_600, lastActiveAt: new Date().toISOString()
      },
      billingHistoryAvailable: true,
      monthToDateCost: 18.28,
      charges: {
        asOf: new Date().toISOString(), computeHours: 173.03, computeCost: 18.27,
        rootStorageGbMonth: 0.03, rootStorageCost: 0.01,
        projects: [
          { id: "plain-union-58175914", name: "ottagive-dev", computeHours: 28.3, computeCost: 3,
            rootStorageGbMonth: 0.009, rootStorageCost: 0.003, totalCost: 3.003 },
          { id: "silent-water-13612749", name: "ottagive-prod", computeHours: 144.73, computeCost: 15.27,
            rootStorageGbMonth: 0.021, rootStorageCost: 0.007, totalCost: 15.277 }
        ]
      },
      currency: "USD"
    }
  };
  }
  return request<AdminCostsResponse>(`/api/admin/costs?${new URLSearchParams({ month })}`);
}

export async function getAdminStaff(): Promise<AdminStaffMember[]> {
  if (IS_MOCK) return [{ uid: "local-super-admin", email: "ian@ottagive.com", role: "superAdmin", active: true, accessVersion: 1, environments: ["dev"], updatedAt: new Date().toISOString(), authDisabled: false, lastSignInAt: new Date().toISOString(), createdAt: new Date().toISOString(), tokensValidAfterAt: new Date().toISOString(), claimRole: "superAdmin", claimAccessVersion: 1, claimsInSync: true }];
  return (await request<{ staff: AdminStaffMember[] }>("/api/admin/staff")).staff;
}

const mockInvitations = new Map<string, StaffInvitation>();
const mockInvitationOperations = new Map<string, { email: string; result: StaffInvitation }>();
export async function getStaffInvitations(): Promise<StaffInvitation[]> {
  if (IS_MOCK) return structuredClone([...mockInvitations.values()]);
  return (await request<{ invitations: StaffInvitation[] }>("/api/admin/staff/invitations")).invitations;
}

export async function createStaffInvitation(email: string, operationId: string): Promise<StaffInvitation> {
  email = email.trim().toLowerCase();
  if (IS_MOCK) {
    const replay = mockInvitationOperations.get(operationId);
    if (replay) { if (replay.email !== email) throw new Error("Operation ID belongs to a different request"); return structuredClone(replay.result); }
    const old = [...mockInvitations.values()].find((invite) => invite.email === email && invite.status === "pending");
    if (old) throw new Error("An invitation is already pending for this email. Copy that invitation or cancel it first.");
    const result: StaffInvitation = { id: operationId, invitationId: operationId, email, role: "cardTemplateCreator", status: "pending",
      createdAt: new Date().toISOString(), expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(), createdByEmail: "ian@ottagive.com",
      inviteUrl: `${window.location.origin}/card-templates?invite=${operationId}` };
    mockInvitations.set(result.id, result); mockInvitationOperations.set(operationId, { email, result: structuredClone(result) }); return result;
  }
  return request<StaffInvitation>("/api/admin/staff/invitations", { method: "POST", headers: { "X-CSRF-Token": await getCsrfToken() }, body: JSON.stringify({ email, operationId }) });
}

export async function revokeStaffInvitation(invite: StaffInvitation, operationId: string): Promise<StaffInvitation> {
  if (IS_MOCK) { const result = { ...invite, status: "revoked" as const }; mockInvitations.set(invite.id, result); return result; }
  return request<StaffInvitation>(`/api/admin/staff/invitations/${invite.id}`, { method: "DELETE", headers: { "X-CSRF-Token": await getCsrfToken() }, body: JSON.stringify({ invitationId: invite.invitationId, operationId }) });
}

export async function updateAdminStaff(input: { email: string; role: AdminRole; active: boolean; reason: string; confirmation: string }): Promise<AdminStaffMember> {
  if (IS_MOCK) return { uid: crypto.randomUUID(), email: input.email, role: input.role, active: input.active, accessVersion: 1, environments: ["dev"], updatedAt: new Date().toISOString(), authDisabled: false, lastSignInAt: null, createdAt: new Date().toISOString(), tokensValidAfterAt: null, claimRole: input.active ? input.role : null, claimAccessVersion: input.active ? 1 : null, claimsInSync: true };
  const csrfToken = await getCsrfToken();
  return request<AdminStaffMember>("/api/admin/staff", {
    method: "PUT",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function revokeAdminStaffSessions(input: { targetUid: string; reason: string; confirmation: string }): Promise<{ uid: string; email: string | null; tokensValidAfterAt: string }> {
  if (IS_MOCK) return { uid: input.targetUid, email: "staff@example.com", tokensValidAfterAt: new Date().toISOString() };
  const csrfToken = await getCsrfToken();
  return request("/api/admin/staff/revoke-sessions", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ ...input, operationId: crypto.randomUUID() }) });
}

export async function getMaintenanceControl(): Promise<MaintenanceControl> {
  if (IS_MOCK) return { enabled: false, expiresAt: null, changedAt: null, changedBy: null, reason: null };
  return (await request<{ maintenance: MaintenanceControl }>("/api/admin/controls")).maintenance;
}

export async function updateMaintenanceControl(input: { enabled: boolean; expiresInMinutes: number | null; reason: string; confirmation: string }): Promise<MaintenanceControl> {
  if (IS_MOCK) return { enabled: input.enabled, expiresAt: input.enabled ? new Date(Date.now() + Number(input.expiresInMinutes) * 60_000).toISOString() : null, changedAt: new Date().toISOString(), changedBy: "ian@ottagive.com", reason: input.reason };
  const csrfToken = await getCsrfToken();
  return request<MaintenanceControl>("/api/admin/controls/maintenance", {
    method: "PUT",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function getVersionPolicy(): Promise<VersionPolicyControl> {
  if (IS_MOCK) return { ...mockVersionPolicy, allowedVersions: [...mockVersionPolicy.allowedVersions] };
  return request<VersionPolicyControl>("/api/admin/controls/version-policy");
}

export async function updateVersionPolicy(input: { action: "add" | "remove"; version: string; expectedRevision: number; reason: string; confirmation: string }): Promise<VersionPolicyControl> {
  if (IS_MOCK) {
    const versions = new Set(mockVersionPolicy.allowedVersions);
    if (input.action === "add") versions.add(input.version); else versions.delete(input.version);
    mockVersionPolicy = { configured: versions.size > 0, revision: mockVersionPolicy.revision + 1, updatedAt: new Date().toISOString(), allowedVersions: [...versions].sort() };
    return getVersionPolicy();
  }
  const csrfToken = await getCsrfToken();
  return request<VersionPolicyControl>("/api/admin/controls/version-policy", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function updateUserBan(input: { firebaseUid: string; banned: boolean; reason: string; confirmation: string }): Promise<{ firebaseUid: string; banned: boolean }> {
  if (IS_MOCK) return { firebaseUid: input.firebaseUid, banned: input.banned };
  const csrfToken = await getCsrfToken();
  return request<{ firebaseUid: string; banned: boolean }>("/api/admin/users/ban", {
    method: "PUT",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function recheckKyc(input: { customerGuid: string; firebaseUid: string; reason: string; confirmation: string }): Promise<{ customerGuid: string; firebaseUid: string; kycState: string | null; identityVerificationState: string | null; identityVerificationOutcome: string | null; appStatus: string; refreshedAt: string }> {
  if (IS_MOCK) return { customerGuid: input.customerGuid, firebaseUid: input.firebaseUid, kycState: "verified", identityVerificationState: "completed", identityVerificationOutcome: "passed", appStatus: "completed", refreshedAt: new Date().toISOString() };
  const csrfToken = await getCsrfToken();
  return request("/api/admin/kyc/recheck", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function getKycCases(): Promise<KycCase[]> {
  if (IS_MOCK) return [];
  return (await request<{ cases: KycCase[] }>("/api/admin/kyc/cases")).cases;
}

export async function getKycCaseDetail(customerGuid: string): Promise<KycCaseDetail> {
  if (IS_MOCK) return { customerGuid, exists: false, notes: [], events: [] };
  return request<KycCaseDetail>(`/api/admin/kyc/cases/${encodeURIComponent(customerGuid)}`);
}

export async function updateKycCase(input: {
  customerGuid: string;
  firebaseUid: string;
  workflowStatus: KycCase["workflowStatus"];
  priority: KycCase["priority"];
  assigneeEmail: string;
  note: string;
  reason: string;
  confirmation: string;
}): Promise<KycCase> {
  if (IS_MOCK) return { ...input, assigneeEmail: input.assigneeEmail || null, latestNote: input.note || null, noteCount: input.note ? 1 : 0, updatedAt: new Date().toISOString(), updatedBy: "local@example.com", escalation: null };
  const csrfToken = await getCsrfToken();
  return request<KycCase>("/api/admin/kyc/cases", {
    method: "PUT",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

/**
 * Opens a compliance escalation with the banking provider. `filingError` is not a
 * failed request: the escalation exists either way, and a non-null value means the
 * ticket has to be filed by hand from the packet the response carries.
 */
export async function openKycEscalation(input: {
  customerGuid: string;
  firebaseUid: string;
  kind: KycEscalationKind;
  context: string;
  canonicalCustomerGuid: string;
  reason: string;
  confirmation: string;
}): Promise<{ customerGuid: string; escalation: KycEscalation; filingError: string | null }> {
  if (IS_MOCK) {
    const now = new Date().toISOString();
    return {
      customerGuid: input.customerGuid,
      filingError: "Zendesk is not configured in this environment",
      escalation: {
        kind: input.kind, status: "awaiting_reference", reference: null, referenceUrl: null, channel: "manual",
        subject: `Mock escalation — customer ${input.customerGuid}`, body: input.context || "No additional context provided.",
        openedAt: now, openedBy: "local@example.com", updatedAt: now, updatedBy: "local@example.com", closedAt: null, outcomeNote: null
      }
    };
  }
  const csrfToken = await getCsrfToken();
  return request("/api/admin/kyc/escalations", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function updateKycEscalation(input: {
  customerGuid: string;
  status: KycEscalation["status"];
  reference: string;
  referenceUrl: string;
  outcomeNote: string;
  reason: string;
  confirmation: string;
}): Promise<{ customerGuid: string; escalation: KycEscalation }> {
  if (IS_MOCK) {
    const now = new Date().toISOString();
    return {
      customerGuid: input.customerGuid,
      escalation: {
        kind: "reinstate", status: input.status, reference: input.reference || null, referenceUrl: input.referenceUrl || null,
        channel: "manual", subject: "Mock escalation", body: "", openedAt: now, openedBy: "local@example.com",
        updatedAt: now, updatedBy: "local@example.com",
        closedAt: input.status === "resolved" || input.status === "declined" ? now : null,
        outcomeNote: input.outcomeNote || null
      }
    };
  }
  const csrfToken = await getCsrfToken();
  return request("/api/admin/kyc/escalations", {
    method: "PUT",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  if (IS_MOCK) return [{ id: "support_follow_up", label: "Support follow-up", title: "A message from Ottagive support", body: "Open the app to review your account and next steps." }];
  return (await request<{ templates: NotificationTemplate[] }>("/api/admin/notification-templates")).templates;
}

export async function sendApprovedNotification(input: { recipientUid: string; templateId: NotificationTemplate["id"]; reason: string; confirmation: string }): Promise<{ successful: number; failed: number; total: number }> {
  if (IS_MOCK) return { successful: 1, failed: 0, total: 1 };
  const csrfToken = await getCsrfToken();
  return request("/api/admin/notifications", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
  });
}

export async function deleteIncomingReport(id: string, expectedRevision: number): Promise<void> {
  if (IS_MOCK) {
    const ticket = mockTickets.find((entry) => entry.id === id);
    if (!ticket || ticket.source !== "mobile" || ticket.status !== "inbox" || ticket.revision !== expectedRevision) throw new Error("The report has already changed.");
    mockTickets = mockTickets.filter((entry) => entry.id !== id);
    return;
  }
  const csrfToken = await getCsrfToken();
  await request(`/api/admin/tickets/${encodeURIComponent(id)}/inbox`, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ expectedRevision, operationId: crypto.randomUUID() }) });
}

export interface CrashReport { id: string; timestamp: string; details: Record<string, unknown> }
export async function getCrashes(kind: string, pageToken?: string): Promise<{ configured: boolean; entries: CrashReport[]; nextPageToken: string | null }> {
  if (IS_MOCK) return { configured: true, entries: [{ id: "example-crash", timestamp: new Date().toISOString(), details: { issue: { title: "Example Flutter error", errorType: "FATAL" }, platform: "IOS", version: { displayVersion: "1.1.5", buildVersion: "170" }, device: { model: "iPhone" }, exceptions: [{ type: "StateError", message: "Example crash for preview", frames: [{ symbol: "ExamplePage.build", line: 42 }] }] } }], nextPageToken: null };
  return request(`/api/admin/crashes?${new URLSearchParams({ kind, ...(pageToken ? { pageToken } : {}) })}`);
}

export async function getGiftFunding(skip = 0): Promise<GiftFundingPage> {
  if (IS_MOCK) return { totalCount: 1, items: skip ? [] : [{
    giftId: "gift-funding-demo", operationId: "send-funding-demo", amountCents: 5000,
    feeCents: 149, source: "bank", instant: false, phase: "funding", giftState: "creating",
    operationState: "processing", errorCode: "funding_from_bank",
    createdAt: "2026-09-05T18:00:00Z", updatedAt: "2026-09-05T18:01:00Z"
  }] };
  return request(`/api/admin/gift-funding?skip=${skip}`);
}

export async function fetchTemplateExportImage(url: string): Promise<Blob> {
  const csrfToken = await getCsrfToken();
  const result = await request<{ contentType: string; data: string }>("/api/admin/card-templates/export-image", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ url }) });
  return new Blob([Uint8Array.from(atob(result.data), (c) => c.charCodeAt(0))], { type: result.contentType });
}
